import axios from 'axios';
import { API_BASE_URL, APP_VERSION } from '../config/constants';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-App-Version': APP_VERSION
  }
});

const GET_COALESCE_MS = 1200;
const inflightGets = new Map();

const getRequestKey = ({ method, url, params, headers } = {}) => {
  const auth =
    headers?.Authorization ||
    headers?.authorization ||
    localStorage.getItem('token') ||
    '';
  return `${String(method || 'get').toLowerCase()}|${url || ''}|${JSON.stringify(params || {})}|${auth}`;
};

['get', 'head', 'options'].forEach((method) => {
  const original = api[method].bind(api);
  api[method] = (url, config = {}) => {
    const key = getRequestKey({
      method,
      url,
      params: config.params,
      headers: config.headers
    });
    const existing = inflightGets.get(key);
    if (existing) return existing;
    const req = original(url, config).finally(() => {
      setTimeout(() => {
        if (inflightGets.get(key) === req) inflightGets.delete(key);
      }, GET_COALESCE_MS);
    });
    inflightGets.set(key, req);
    return req;
  };
});

['post', 'put', 'patch', 'delete'].forEach((method) => {
  const original = api[method].bind(api);
  api[method] = (...args) => {
    inflightGets.clear();
    return original(...args);
  };
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
      const h = config.headers;
      if (typeof h.delete === 'function') {
        h.delete('Content-Type');
      } else {
        delete h['Content-Type'];
      }
    }
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log network errors for debugging
    if (!error.response) {
      console.error('Network Error:', error.message);
      console.error('API URL:', API_BASE_URL);
      console.error('Full Error:', error);
    }
    
    if (error.response?.status === 401) {
      const requestUrl = (error.config?.url || '').toString();
      const isAdminRequest = requestUrl.includes('admin');
      const isAuthRequest = requestUrl.includes('auth') || requestUrl.includes('sms');
      
      // Don't redirect on auth endpoints (login, verify-otp, etc) - let the component handle it
      if (isAuthRequest) {
        console.log('401 on auth endpoint, not redirecting:', requestUrl);
        return Promise.reject(error);
      }
      
      // Check user before clearing (for non-admin requests)
      let isAdminUser = false;
      try {
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        isAdminUser = user && (user.role === 'admin' || user.role === 'branchadmin' || user.role === 'brandadmin' || user.role === 'superadmin');
      } catch (e) {
        // ignore
      }
      
      console.log('401 Unauthorized - clearing auth and redirecting');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Admin request 401 or admin user -> admin login. Else -> QR login
      if (isAdminRequest || isAdminUser) {
        window.location.href = '/admin/login';
      } else {
        window.location.href = '/qr-login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
